import { request } from "./client";
export interface AppointmentSpec {
  version: 1; kind: "request_appointment" | "relay_message"; goal: string;
  /** request_appointment */
  business: { name: string; phone: string };
  /** relay_message */
  recipient?: { name: string; phone: string }; message?: string; allow_reply?: boolean; may_name_owner?: boolean;
  timezone: string; windows: { start: string; end: string }[];
  deadline: string; max_attempts: number; instructions: string;
  approved_terms?: string[]; approved_requirements?: string[];
}
/** A rule result for an open record (F02): errors need corrected evidence, changes need approved terms, warnings need acknowledgement. */
export interface EvidenceFinding { code: string; severity: "error" | "change" | "warning"; message: string; action: string }
export interface JobEvidence {
  outcome: "appointment_confirmed" | "no_effect" | "failed";
  reference: string; note: string; start?: string; end?: string; verified_by?: string;
  supporting_kind?: string; evidence_id?: string;
}
export interface EvidenceRecord {
  id: string; kind: "conversation_claim" | "business_confirmation" | "provider_receipt" | "owner_attestation";
  source: string; outcome: string; status: "candidate" | "needs_owner" | "accepted" | "rejected" | "superseded";
  appointment_start?: string; appointment_end?: string; confirmation_ref?: string; charges?: string; requirements?: string; reply?: string;
  verification_method: string; validation: { fits_window: boolean; issues: string[] }; created_at: string;
  /** Present on open records: what acceptance would find now. */
  findings?: EvidenceFinding[]; superseded_by?: string; superseded_reason?: string;
}
export interface JobDecision {
  id: string; status: "pending" | "accepted" | "declined" | "superseded"; spec_version: number;
  proposal: { slot?: { start: string; end: string }; charges?: string | null; requirements?: string | null; note?: string | null };
  affected_constraints: string[]; created_at: string; resulting_spec_version?: number;
}
export interface VoiceJob {
  id: string; spec: AppointmentSpec; status: string; revision: number; created_at: string;
  spec_hash?: string; spec_version?: number; created_via?: string;
  attempts?: { id: string; attempt_number: number; dispatch_status: string; session_status?: string;
    runtime_stage?: string; call_session_id?: string; reconciliation?: string; terminated_at?: string;
    dispatch_receipt?: { session_id?: string; call_id?: string; error?: string }; resolution?: JobEvidence }[];
  events?: { revision: number; actor: string; status: string; created_at: string; detail?: Record<string, unknown> }[];
  evidence?: EvidenceRecord[];
  decisions?: JobDecision[];
  calendar?: { status: string; last_error?: string; provider_event_id?: string } | null;
  commands?: { id: string; status: string; tries: number; last_error?: string }[];
  /** Reported separately: what the business agreed, whether you accepted it, and the calendar. */
  outcome_summary?: { business_agreement: string | null; owner_accepted: boolean; calendar: string | null };
}
export interface VoiceMessage {
  id: string; caller_name: string; callback_number?: string; message: string; urgency: string;
  created_at: string; acknowledged_at?: string;
}
const jobPath = (job: VoiceJob) => `/voice/jobs/${encodeURIComponent(job.id)}`;
export const voiceJobs = {
  list: () => request<{ jobs: VoiceJob[] }>("/voice/jobs"),
  get: (id: string) => request<VoiceJob>(`/voice/jobs/${encodeURIComponent(id)}`),
  create: (request_key: string, spec: AppointmentSpec) => request<VoiceJob>("/voice/jobs", { method: "POST", body: JSON.stringify({ request_key, spec }) }),
  decide: (job: VoiceJob, action: string, evidence?: JobEvidence) => request<VoiceJob>(`${jobPath(job)}/${action}`, {
    method: "POST",
    // Approval is bound to the terms on screen: a changed spec is refused.
    body: JSON.stringify({ revision: job.revision, evidence, ...(action === "approve" && job.spec_hash ? { spec_hash: job.spec_hash } : {}) }),
  }),
  decideChange: (job: VoiceJob, decisionId: string, choice: "accept" | "decline") => request<VoiceJob>(`${jobPath(job)}/decisions/${encodeURIComponent(decisionId)}`, {
    method: "POST", body: JSON.stringify({ revision: job.revision, choice }),
  }),
  acceptEvidence: (job: VoiceJob, evidenceId: string, acknowledge: string[] = []) => request<VoiceJob>(`${jobPath(job)}/evidence/${encodeURIComponent(evidenceId)}/accept`, {
    method: "POST", body: JSON.stringify({ revision: job.revision, ...(acknowledge.length ? { acknowledge } : {}) }),
  }),
  reconcile: (job: VoiceJob) => request<{ result: string }>(`${jobPath(job)}/reconcile`, { method: "POST" }),
  messages: () => request<{ messages: VoiceMessage[] }>("/voice/messages"),
  acknowledge: (id: string) => request(`/voice/messages/${encodeURIComponent(id)}/acknowledge`, { method: "POST" }),
};
